import { useEffect, useMemo, useRef, useState } from "react";
import { Link, createFileRoute, useLocation } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { User } from "@rago/web-shared";
import { api } from "../../../lib/apiClient";
import { useAuth } from "../../../lib/useAuth";
import { adoptProfile, describeAccountError } from "../../../lib/accountActions";
import {
  accountErrors,
  accountPatch,
  checkAccountField,
  formFromUser,
  sameForm,
  type AccountField,
} from "../../../lib/accountForm";
import {
  errorFromServer,
  errorsOnBlur,
  errorsOnSubmit,
  errorsOnType,
  firstErrorField,
  type ShownErrors,
} from "../../../lib/authForm";
import {
  SYNC_SETTLE_MS,
  WATCH_HISTORY_KEY,
  WATCH_STATE_SYNC_KEY,
  WATCH_STATS_KEY,
  nudgeServerGrants,
  saveWatchStateSync,
  useWatchStateSync,
} from "../../../lib/profile";
import { shortDate } from "../../../lib/timeAgo";
import { DeleteAccountDialog } from "../../../components/account/delete-account-dialog";
import { EmailChangeDialog } from "../../../components/account/email-change-dialog";
import { EmailStatus, emailStatusId } from "../../../components/account/email-status";
import { PencilIcon, TrashIcon, XIcon } from "../../../components/icons";
import { Checkbox, Chip, ErrorNote, Field } from "../../../components/ui";
import {
  LeadRow,
  ReadOnlyValue,
  SheetHeader,
  SheetSection,
  UnsavedBar,
  WellAction,
  WellWithAction,
  saveOnEnter,
  useDraft,
  useLastTouched,
} from "../../../components/settings-sheet";

export const Route = createFileRoute("/_authed/settings/account")({
  component: Account,
});

function Account() {
  const state = useAuth();
  const user = state.status === "signedIn" ? state.user : null;
  const qc = useQueryClient();

  // The profile's "Edit profile" deep-links here as #displayName: bring the
  // field into view clear of the floating bar and hand it the focus.
  const hash = useLocation({ select: (l) => l.hash });
  useEffect(() => {
    if (hash !== "displayName") return;
    const el = document.getElementById("displayName");
    if (!el) return;
    el.scrollIntoView({ block: "center" });
    el.focus({ preventScroll: true });
  }, [hash]);

  // One draft seeded from the account, following it while it is still clean
  // (settings-sheet useDraft; the server pages' baseline→draft→patch
  // pattern, lib/accountForm).
  const baseline = useMemo(() => (user ? formFromUser(user) : null), [user]);
  const [draft, setDraft] = useDraft(baseline, sameForm);

  // The watch-state consent is the draft's other half, saved by its own
  // PATCH: null = not touched, so the query's refetch never undoes an edit.
  const sync = useWatchStateSync();
  const [syncDraft, setSyncDraft] = useState<boolean | null>(null);

  const [saved, setSaved] = useState(false);
  // Each field's line: from leaving it, from a submit, or the API's word on
  // what was typed (a taken username). Typing only ever takes a line away.
  const [errors, setErrors] = useState<ShownErrors<AccountField>>({});
  // A failure that is no field's (the network, a rate limit): the pill's.
  const [failure, setFailure] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);

  // The username is a well until Edit turns it into an input; the display
  // name is an input all along.
  const [editingUsername, setEditingUsername] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const displayNameRef = useRef<HTMLInputElement>(null);
  const usernameEditRef = useRef<HTMLButtonElement>(null);
  const emailEditRef = useRef<HTMLButtonElement>(null);

  // The well and the input replace each other under one id, so the focus
  // has to be carried across: into the input, its value selected, when the
  // edit opens; back to Edit when it closes under a focus it was holding.
  const focusEditAfterClose = useRef(false);
  useEffect(() => {
    if (editingUsername) {
      usernameRef.current?.focus();
      usernameRef.current?.select();
    } else if (focusEditAfterClose.current) {
      focusEditAfterClose.current = false;
      usernameEditRef.current?.focus();
    }
  }, [editingUsername]);

  // The control changed last gets the focus back when the pill leaves; the
  // username's input goes when its edit closes, and Edit takes it then.
  const touched = useLastTouched(() => usernameEditRef.current);

  if (!user || !baseline) return null;
  const seed = baseline;
  const form = draft ?? seed;
  const patch = accountPatch(seed, form);
  const profileDirty = Object.keys(patch).length > 0;
  const syncSaved = sync.data?.enabled ?? false;
  const syncWanted = syncDraft ?? syncSaved;
  const syncDirty = sync.data !== undefined && syncWanted !== syncSaved;
  const dirty = profileDirty || syncDirty;

  function edit(field: AccountField, value: string) {
    setDraft((d) => ({ ...(d ?? seed), [field]: value }));
    setErrors((prev) => errorsOnType(prev, field, checkAccountField(seed, field, value)));
    setFailure(null);
    setSaved(false);
  }

  function editSync(next: boolean) {
    setSyncDraft(next);
    setFailure(null);
    setSaved(false);
  }

  function leave(field: AccountField) {
    const value = form[field];
    setErrors((prev) => errorsOnBlur(prev, field, value, checkAccountField(seed, field, value)));
  }

  function focusField(field: AccountField) {
    (field === "username" ? usernameRef : displayNameRef).current?.focus();
  }

  function closeUsernameEdit(focusEdit: boolean) {
    focusEditAfterClose.current = focusEdit;
    setEditingUsername(false);
  }

  function cancelUsernameEdit() {
    setDraft((d) => ({ ...(d ?? seed), username: seed.username }));
    setErrors((prev) => errorsOnType(prev, "username", undefined));
    // The input and Cancel both go, and one of them held the focus.
    closeUsernameEdit(true);
  }

  function revert() {
    setDraft(null);
    setSyncDraft(null);
    setErrors({});
    setFailure(null);
    // An open edit closes with it, as after a save.
    if (editingUsername) closeUsernameEdit(document.activeElement === usernameRef.current);
  }

  async function save() {
    if (busy) return;
    const found = accountErrors(seed, form);
    const first = firstErrorField(found);
    setErrors(errorsOnSubmit(found));
    setFailure(null);
    setSaved(false);
    if (first) {
      focusField(first);
      return;
    }
    // Enter in a field with nothing to save.
    if (!dirty) return;
    setBusy(true);
    try {
      // The profile first: a name the API refuses stops the save before the
      // consent changes. When the consent then fails, the profile is saved
      // and Save retries the consent alone.
      if (profileDirty) {
        const updated = await api<User>("/v1/user", { method: "PATCH", body: patch });
        adoptProfile(updated);
        // Our own username is also shown from the servers list (General's
        // "Claimed by"). Not the shares: their rows carry other people's names.
        if ("username" in patch) void qc.invalidateQueries({ queryKey: ["resources"] });
        // An open edit closes, saved or untouched; Enter in its input leaves
        // the focus in a field that is about to go, so Edit takes it.
        if (editingUsername) closeUsernameEdit(document.activeElement === usernameRef.current);
        setDraft(null); // snap to the server's normalized copy
      }
      if (syncDirty) {
        qc.setQueryData(WATCH_STATE_SYNC_KEY, await saveWatchStateSync(syncWanted));
        setSyncDraft(null);
        // Online servers re-sync their grants now rather than on the next
        // heartbeat, so the backfill upload starts within seconds; history
        // and stats refetch once it has had time to land.
        nudgeServerGrants();
        const refresh = () => {
          void qc.invalidateQueries({ queryKey: WATCH_HISTORY_KEY });
          void qc.invalidateQueries({ queryKey: WATCH_STATS_KEY });
        };
        refresh();
        setTimeout(refresh, SYNC_SETTLE_MS);
      }
      setSaved(true);
    } catch (err) {
      const info = describeAccountError(err, "profile");
      if (info.field === "username" || info.field === "displayName") {
        setErrors(errorFromServer<AccountField>(info.field, info.message));
        focusField(info.field);
      } else {
        setFailure(new Error(info.message));
      }
    } finally {
      setBusy(false);
    }
  }

  // Enter in either field saves the page.
  const enterSaves = saveOnEnter(() => void save());

  return (
    // .rago-sheet-fill: the root fills the sheet, so the pill docks at its foot.
    <div className="rago-sheet-fill">
      <SheetHeader title="Account" />
      {/* Every control's change bubbles to here: the one changed last. */}
      <div onChange={touched.onChange}>
        <SheetSection title="Personal information">
          <Field
            label="Username"
            htmlFor="username"
            error={errors.username?.message}
            alert={errors.username?.alert}
          >
            {editingUsername ? (
              <WellWithAction
                action={
                  <WellAction icon={<XIcon size={13} />} keepFocus onClick={cancelUsernameEdit}>
                    Cancel
                  </WellAction>
                }
              >
                <input
                  ref={usernameRef}
                  id="username"
                  className="rago-input rago-input-action"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={form.username}
                  onChange={(e) => edit("username", e.target.value)}
                  onBlur={() => leave("username")}
                  onKeyDown={(e) => {
                    if (e.key !== "Escape") return enterSaves(e);
                    e.preventDefault();
                    cancelUsernameEdit();
                  }}
                  aria-invalid={errors.username ? true : undefined}
                  aria-describedby={errors.username ? "username-error" : undefined}
                />
              </WellWithAction>
            ) : (
              <ReadOnlyValue
                id="username"
                label="Username"
                value={user.username}
                action={
                  <WellAction
                    ref={usernameEditRef}
                    icon={<PencilIcon size={13} />}
                    label="Edit username"
                    onClick={() => setEditingUsername(true)}
                  >
                    Edit
                  </WellAction>
                }
              />
            )}
          </Field>
          <Field label="Email" htmlFor="email">
            <ReadOnlyValue
              id="email"
              label="Email"
              value={user.email}
              describedBy={emailStatusId(user)}
              badge={
                user.emailVerified ? (
                  <Chip tone="ok">Verified</Chip>
                ) : (
                  <Chip tone="warn">Unverified</Chip>
                )
              }
              action={
                <WellAction
                  ref={emailEditRef}
                  icon={<PencilIcon size={13} />}
                  label="Change email"
                  hasPopup="dialog"
                  onClick={(e) => {
                    // The dialog hands the focus back to what held it when it
                    // opened; a click does not focus a button everywhere.
                    e.currentTarget.focus();
                    setEmailOpen(true);
                  }}
                >
                  Edit
                </WellAction>
              }
            />
            <EmailStatus user={user} onCancelled={() => emailEditRef.current?.focus()} />
          </Field>
          <Field
            label="Display name"
            htmlFor="displayName"
            hint="Shown to people on your servers."
            error={errors.displayName?.message}
            alert={errors.displayName?.alert}
          >
            <input
              ref={displayNameRef}
              id="displayName"
              className="rago-input"
              value={form.displayName}
              onChange={(e) => edit("displayName", e.target.value)}
              onBlur={() => leave("displayName")}
              onKeyDown={enterSaves}
              aria-invalid={errors.displayName ? true : undefined}
              aria-describedby={errors.displayName ? "displayName-error" : undefined}
            />
          </Field>
        </SheetSection>

        <WatchStateSyncSection sync={sync} checked={syncWanted} onChange={editSync} />
      </div>

      <SheetSection title="Danger zone" tone="accent">
        <LeadRow
          title="Delete account"
          hint="Permanently removes your account, watch history and sign-ins. This can't be undone."
          action={
            <button
              type="button"
              className="rago-btn rago-btn-danger rago-btn-sm"
              aria-haspopup="dialog"
              onClick={(e) => {
                e.currentTarget.focus(); // see the email well's Edit
                setDeleteOpen(true);
              }}
            >
              <TrashIcon size={14} />
              Delete account
            </button>
          }
        />
      </SheetSection>

      {/* Beside the fields, not among them: the dialogs portal to <body>, but
          React's events still bubble along THIS tree, and a field typed in
          there is none of the page's. */}
      <EmailChangeDialog open={emailOpen} user={user} onClose={() => setEmailOpen(false)} />
      <DeleteAccountDialog open={deleteOpen} user={user} onClose={() => setDeleteOpen(false)} />

      {/* The root's direct child (its dock rides at the sheet's foot), and
          always rendered, as UnsavedBar asks. Save is never disabled for a
          wrong value: it is what judges, and it puts the focus on the first
          field it found wrong. */}
      <UnsavedBar
        dirty={dirty}
        saving={busy}
        saved={saved}
        error={failure?.message ?? null}
        onSave={() => void save()}
        onRevert={revert}
        onFocusLost={touched.focus}
      />
    </div>
  );
}

/**
 * The account-level "Sync my watch state" consent (Plex's Sync My Watch
 * State and Ratings). Part of the page's draft (Kenny, 2026-10: saved from
 * the pill, where it used to apply the moment the box was ticked): the box
 * shows the draft, while the Active chip and "On since" say what is SAVED —
 * the servers sync by that until Save. Its PATCH carries only this flag.
 */
function WatchStateSyncSection(props: {
  sync: ReturnType<typeof useWatchStateSync>;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const enabled = props.sync.data?.enabled ?? false;
  const since = props.sync.data?.since ?? null;

  return (
    <SheetSection
      title="Watch history"
      action={
        <Link to="/profile" className="rago-btn rago-btn-quiet rago-btn-sm select-none">
          View profile
        </Link>
      }
    >
      <ErrorNote error={props.sync.error} />
      <Checkbox
        id="watchStateSync"
        checked={props.checked}
        // Nothing to edit until the saved value is known.
        disabled={!props.sync.data}
        onChange={props.onChange}
        label={
          <span className="inline-flex flex-wrap items-center gap-2">
            Sync my watch state
            {enabled ? <Chip tone="ok">Active</Chip> : null}
          </span>
        }
        hint={`Keep what you've watched in sync across every server you use. Your profile's watch history and stats come from this. Servers that are online pick it up right away; the rest on their next check-in.${
          enabled && since ? ` On since ${shortDate(since)}.` : ""
        }`}
      />
    </SheetSection>
  );
}
